"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { BookOpen, FlaskConical, Folder, FolderKanban, Lightbulb, Target } from "lucide-react";
import { EntityActionsMenu } from "@/components/entity-actions-menu";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useNavigationData } from "@/hooks/useNavigationData";
import { buildProjectConversationsPath, buildProjectHomePath } from "@/lib/entityDeepLinks";
import { deleteExperiment as deleteExperimentService } from "@/services/experimentService";
import { useBreadcrumbStore } from "@/stores/useBreadcrumbStore";
import { useExperimentStore } from "@/stores/useExperimentStore";
import { useLearningStore } from "@/stores/useLearningStore";
import { useProjectStore } from "@/stores/useProjectStore";
import { getErrorMessage } from "@/types/problem-detail";
import { toast } from "sonner";
function ExperimentActionsInBreadcrumb() {
  const $ = _c(9);
  const {
    projectId,
    experimentId
  } = useNavigationData();
  const currentExperimentTitle = useBreadcrumbStore(_temp);
  const router = useRouter();
  const clearExperimentById = useExperimentStore(_temp2);
  const clearExperiments = useExperimentStore(_temp3);
  if (!projectId || !experimentId) {
    return null;
  }
  let t0;
  if ($[0] !== clearExperimentById || $[1] !== clearExperiments || $[2] !== projectId || $[3] !== router) {
    t0 = async id => {
      ;
      try {
        await deleteExperimentService(projectId, id);
        clearExperimentById(id);
        clearExperiments(projectId);
        router.push(buildProjectHomePath(projectId));
      } catch (t1) {
        const error = t1;
        toast.error(getErrorMessage(error, "Failed to delete experiment."));
      }
    };
    $[0] = clearExperimentById;
    $[1] = clearExperiments;
    $[2] = projectId;
    $[3] = router;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const handleDelete = t0;
  const t1 = currentExperimentTitle ?? "Experiment";
  let t2;
  if ($[5] !== experimentId || $[6] !== handleDelete || $[7] !== t1) {
    t2 = <EntityActionsMenu ariaLabel="Experiment actions" entityId={experimentId} entityTitle={t1} entityType="Experiment" onDelete={handleDelete} className="ml-2 shrink-0" />;
    $[5] = experimentId;
    $[6] = handleDelete;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function _temp3(s_1) {
  return s_1.clearExperiments;
}
function _temp2(s_0) {
  return s_0.clearExperimentById;
}
function _temp(s) {
  return s.currentExperimentTitle;
}
function LearningActionsInBreadcrumb() {
  const $ = _c(8);
  const {
    projectId,
    learningId
  } = useNavigationData();
  const currentLearningTitle = useBreadcrumbStore(_temp4);
  const router = useRouter();
  const deleteLearning = useLearningStore(_temp5);
  if (!projectId || !learningId) {
    return null;
  }
  let t0;
  if ($[0] !== deleteLearning || $[1] !== projectId || $[2] !== router) {
    t0 = async id => {
      ;
      try {
        await deleteLearning(projectId, id);
        router.push(buildProjectHomePath(projectId));
      } catch (t1) {
        const error = t1;
        toast.error(getErrorMessage(error, "Failed to delete learning."));
      }
    };
    $[0] = deleteLearning;
    $[1] = projectId;
    $[2] = router;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleDelete = t0;
  const t1 = currentLearningTitle ?? "Learning";
  let t2;
  if ($[4] !== handleDelete || $[5] !== learningId || $[6] !== t1) {
    t2 = <EntityActionsMenu ariaLabel="Learning actions" entityId={learningId} entityTitle={t1} entityType="Learning" onDelete={handleDelete} className="ml-2 shrink-0" />;
    $[4] = handleDelete;
    $[5] = learningId;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp5(s_0) {
  return s_0.deleteLearning;
}
function _temp4(s) {
  return s.currentLearningTitle;
}
export function EnhancedBreadcrumbs() {
  const $ = _c(40);
  const {
    currentProject,
    projectId: projectIdFromNav,
    section,
    experimentId,
    learningId,
    insightId,
    hypothesisId
  } = useNavigationData();
  const projects = useProjectStore(_temp6);
  const currentExperimentTitle = useBreadcrumbStore(_temp7);
  const currentLearningTitle = useBreadcrumbStore(_temp8);
  const currentInsightTitle = useBreadcrumbStore(_temp9);
  const currentHypothesisLabel = useBreadcrumbStore(_temp10);
  const pathname = usePathname();
  const isWorkspaceOverview = pathname === "/dashboard";
  let t0;
  if ($[0] !== currentProject || $[1] !== projectIdFromNav || $[2] !== projects) {
    t0 = currentProject ?? (projectIdFromNav ? projects.find(p => p.id === projectIdFromNav) : null);
    $[0] = currentProject;
    $[1] = projectIdFromNav;
    $[2] = projects;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const displayProject = t0;
  const projectId = projectIdFromNav ?? displayProject?.id ?? null;
  const showProjectSegment = Boolean(displayProject || projectId);
  const projectLabel = displayProject ? displayProject.title : "Loading...";
  const isProjectLink = section !== null && section !== "overview";
  let t1;
  if ($[4] !== isWorkspaceOverview) {
    t1 = <BreadcrumbItem>{isWorkspaceOverview ? <BreadcrumbPage className="flex items-center gap-2"><FolderKanban className="h-4 w-4" />Projects</BreadcrumbPage> : <BreadcrumbLink asChild={true}><Link href="/dashboard" className="flex items-center gap-2"><FolderKanban className="h-4 w-4" />Projects</Link></BreadcrumbLink>}</BreadcrumbItem>;
    $[4] = isWorkspaceOverview;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== displayProject || $[7] !== isProjectLink || $[8] !== projectId || $[9] !== projectLabel || $[10] !== section || $[11] !== showProjectSegment) {
    t2 = showProjectSegment ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem>{displayProject && isProjectLink ? <BreadcrumbLink asChild={true}><Link href={`/dashboard/projects/${displayProject.id}`} className="flex min-w-0 items-center gap-2" title={projectLabel}><Folder className="h-4 w-4 shrink-0" /><span className="max-w-[7rem] truncate sm:max-w-none">{projectLabel}</span></Link></BreadcrumbLink> : projectId && isProjectLink ? <BreadcrumbLink asChild={true}><Link href={`/dashboard/projects/${projectId}`} className="flex min-w-0 items-center gap-2 text-muted-foreground" title={projectLabel}><Folder className="h-4 w-4 shrink-0" /><span className="max-w-[7rem] truncate sm:max-w-none">{projectLabel}</span></Link></BreadcrumbLink> : displayProject && (!section || section === "overview") ? <BreadcrumbPage className="flex min-w-0 items-center gap-2"><Folder className="h-4 w-4 shrink-0" /><span className="max-w-[7rem] truncate sm:max-w-none" title={projectLabel}>{projectLabel}</span></BreadcrumbPage> : <span className="flex min-w-0 items-center gap-2 text-muted-foreground"><Folder className="h-4 w-4 shrink-0" /><span className="max-w-[7rem] truncate sm:max-w-none" title={projectLabel}>{projectLabel}</span></span>}</BreadcrumbItem></> : null;
    $[6] = displayProject;
    $[7] = isProjectLink;
    $[8] = projectId;
    $[9] = projectLabel;
    $[10] = section;
    $[11] = showProjectSegment;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== currentExperimentTitle || $[14] !== experimentId || $[15] !== projectId || $[16] !== section) {
    t3 = section === "experiments" && projectId ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem>{experimentId ? <BreadcrumbLink asChild={true}><Link href={buildProjectConversationsPath(projectId)} className="flex items-center gap-2"><FlaskConical className="h-4 w-4" />Experiments</Link></BreadcrumbLink> : <BreadcrumbPage className="flex items-center gap-2"><FlaskConical className="h-4 w-4" />Experiments</BreadcrumbPage>}</BreadcrumbItem>{experimentId ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem className="min-w-0"><div className="flex min-w-0 items-center gap-2" aria-current="page"><FlaskConical className="h-4 w-4 shrink-0" /><span className="max-w-[7rem] truncate sm:max-w-none" title={currentExperimentTitle ?? undefined}>{currentExperimentTitle ?? "Experiment"}</span><ExperimentActionsInBreadcrumb /></div></BreadcrumbItem></> : null}</> : null;
    $[13] = currentExperimentTitle;
    $[14] = experimentId;
    $[15] = projectId;
    $[16] = section;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== currentHypothesisLabel || $[19] !== hypothesisId || $[20] !== projectId || $[21] !== section) {
    t4 = section === "hypotheses" && projectId ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem>{hypothesisId ? <BreadcrumbLink asChild={true}><Link href={buildProjectConversationsPath(projectId)} className="flex items-center gap-2"><Target className="h-4 w-4" />Hypotheses</Link></BreadcrumbLink> : <BreadcrumbPage className="flex items-center gap-2"><Target className="h-4 w-4" />Hypotheses</BreadcrumbPage>}</BreadcrumbItem>{hypothesisId ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem className="min-w-0"><BreadcrumbPage className="flex min-w-0 items-center gap-2"><Target className="h-4 w-4 shrink-0" /><span className="max-w-[7rem] truncate sm:max-w-none" title={currentHypothesisLabel ?? undefined}>{currentHypothesisLabel ?? "Hypothesis"}</span></BreadcrumbPage></BreadcrumbItem></> : null}</> : null;
    $[18] = currentHypothesisLabel;
    $[19] = hypothesisId;
    $[20] = projectId;
    $[21] = section;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== currentInsightTitle || $[24] !== insightId || $[25] !== projectId || $[26] !== section) {
    t5 = section === "insights" && projectId ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem>{insightId ? <BreadcrumbLink asChild={true}><Link href={buildProjectConversationsPath(projectId)} className="flex items-center gap-2"><Lightbulb className="h-4 w-4" />Insights</Link></BreadcrumbLink> : <BreadcrumbPage className="flex items-center gap-2"><Lightbulb className="h-4 w-4" />Insights</BreadcrumbPage>}</BreadcrumbItem>{insightId ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem className="min-w-0"><BreadcrumbPage className="flex min-w-0 items-center gap-2"><Lightbulb className="h-4 w-4 shrink-0" /><span className="max-w-[7rem] truncate sm:max-w-none" title={currentInsightTitle ?? undefined}>{currentInsightTitle ?? "Insight"}</span></BreadcrumbPage></BreadcrumbItem></> : null}</> : null;
    $[23] = currentInsightTitle;
    $[24] = insightId;
    $[25] = projectId;
    $[26] = section;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== currentLearningTitle || $[29] !== learningId || $[30] !== projectId || $[31] !== section) {
    t6 = section === "learnings" && projectId ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem>{learningId ? <BreadcrumbLink asChild={true}><Link href={buildProjectConversationsPath(projectId)} className="flex items-center gap-2"><BookOpen className="h-4 w-4" />Learnings</Link></BreadcrumbLink> : <BreadcrumbPage className="flex items-center gap-2"><BookOpen className="h-4 w-4" />Learnings</BreadcrumbPage>}</BreadcrumbItem>{learningId ? <><BreadcrumbSeparator className="hidden md:block" /><BreadcrumbItem className="min-w-0"><div className="flex min-w-0 items-center gap-2" aria-current="page"><BookOpen className="h-4 w-4 shrink-0" /><span className="max-w-[7rem] truncate sm:max-w-none" title={currentLearningTitle ?? undefined}>{currentLearningTitle ?? "Learning"}</span><LearningActionsInBreadcrumb /></div></BreadcrumbItem></> : null}</> : null;
    $[28] = currentLearningTitle;
    $[29] = learningId;
    $[30] = projectId;
    $[31] = section;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== t1 || $[34] !== t2 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6) {
    t7 = <Breadcrumb><BreadcrumbList>{t1}{t2}{t3}{t4}{t5}{t6}</BreadcrumbList></Breadcrumb>;
    $[33] = t1;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  return t7;
}
function _temp10(s_3) {
  return s_3.currentHypothesisLabel;
}
function _temp9(s_2) {
  return s_2.currentInsightTitle;
}
function _temp8(s_1) {
  return s_1.currentLearningTitle;
}
function _temp7(s_0) {
  return s_0.currentExperimentTitle;
}
function _temp6(s) {
  return s.projects;
}